import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api } from "@/lib/appApi";

import PublicLayout from "@/components/public/PublicLayout";
import Home from "@/pages/public/Home";
import BeritaList from "@/pages/public/BeritaList";
import BidangPage from "@/pages/public/BidangPage";
import Galeri from "@/pages/public/Galeri";
import Tentang from "@/pages/public/Tentang";
import ArticleDetail from "@/pages/public/ArticleDetail";
import SearchPage from "@/pages/public/SearchPage";
import NotFound from "@/pages/public/NotFound";

import AdminLayout from "@/components/admin/AdminLayout";
import Login from "@/pages/admin/Login";
import Dashboard from "@/pages/admin/Dashboard";
import ContentList from "@/pages/admin/ContentList";
import ContentForm from "@/pages/admin/ContentForm";
import ReviewQueue from "@/pages/admin/ReviewQueue";
import MediaLibrary from "@/pages/admin/MediaLibrary";
import OrgTopics from "@/pages/admin/OrgTopics";
import Users from "@/pages/admin/Users";
import Settings from "@/pages/admin/Settings";
import ArticlePreview from "@/pages/admin/ArticlePreview";
import Comments from "@/pages/admin/Comments";

function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = location.hash || "";
    const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    (async () => {
      try {
        const res = await api.post("/auth/session", { session_id: sid });
        setUser(res.data.user);
      } catch (e) {
        // ignore; will land on login
      } finally {
        window.history.replaceState(null, "", "/admin");
        navigate("/admin", { replace: true });
      }
    })();
  }, [location.hash, navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-dlh-green text-white">
      <p className="text-lg">Memproses masuk…</p>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-dlh-greenPale text-dlh-green">Memuat…</div>;
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/berita" element={<BeritaList />} />
        <Route path="/berita/:slug" element={<ArticleDetail />} />
        <Route path="/bidang/:slug" element={<BidangPage />} />
        <Route path="/galeri" element={<Galeri />} />
        <Route path="/tentang" element={<Tentang />} />
        <Route path="/cari" element={<SearchPage />} />
      </Route>

      <Route path="/admin/login" element={<Login />} />
      <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="konten" element={<ContentList />} />
        <Route path="konten/baru" element={<ContentForm />} />
        <Route path="konten/:id" element={<ContentForm />} />
        <Route path="konten/:id/pratinjau" element={<ArticlePreview />} />
        <Route path="antrean" element={<ReviewQueue />} />
        <Route path="komentar" element={<Comments />} />
        <Route path="media" element={<MediaLibrary />} />
        <Route path="organisasi" element={<OrgTopics />} />
        <Route path="pengguna" element={<Users />} />
        <Route path="pengaturan" element={<Settings />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
